import React, { useState, useEffect } from 'react';
import './OrderManagement.css';
import storeService from '../services/storeService';

interface OrderManagementProps {
  storeData: any;
}

const OrderManagement: React.FC<OrderManagementProps> = ({ storeData }) => {
  const [orders, setOrders] = useState<any[]>([]);
  const [filterStatus, setFilterStatus] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [prepTime, setPrepTime] = useState(15);
  const [rejectReason, setRejectReason] = useState('');
  
  // Custom Order Bill State
  const [showCustomBill, setShowCustomBill] = useState(false);
  const [customBillOrder, setCustomBillOrder] = useState<any>(null);
  const [billItems, setBillItems] = useState<{name: string, quantity: number, price: number, isAvailable: boolean}[]>([]);
  const [additionalCharges, setAdditionalCharges] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [billNotes, setBillNotes] = useState('');

  // Categorized orders
  const pendingOrders = orders.filter(o => o.status === 'Pending' || o.status === 'pending');
  const customOrders = orders.filter(o => o.isCustomOrder && ['Requested', 'Store Reviewing', 'Bill Sent'].includes(o.customOrderStatus));
  const ongoingOrders = orders.filter(o => ['Confirmed', 'confirmed', 'Preparing', 'preparing'].includes(o.status));
  const readyOrders = orders.filter(o => o.status === 'ready' || o.status === 'Ready');
  const completedOrders = orders.filter(o => ['Delivered', 'delivered', 'rejected', 'Rejected', 'Cancelled'].includes(o.status));

  useEffect(() => {
    fetchOrders();
    const interval = setInterval(fetchOrders, 5000); // Refresh every 5 seconds for real-time
    return () => clearInterval(interval);
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await storeService.getStoreOrders(storeData._id);
      setOrders(response.orders || []);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptOrder = async (orderId: string) => {
    try {
      await storeService.acceptOrder(orderId, prepTime);
      alert(`Order accepted! Preparation time: ${prepTime} minutes`);
      fetchOrders();
      setSelectedOrder(null);
    } catch (error) {
      console.error('Error accepting order:', error);
      alert('Failed to accept order');
    }
  };

  const handleRejectOrder = async (orderId: string) => {
    if (!rejectReason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }
    
    try {
      await storeService.rejectOrder(orderId, rejectReason);
      alert('Order rejected');
      fetchOrders();
      setSelectedOrder(null);
      setRejectReason('');
    } catch (error) {
      console.error('Error rejecting order:', error);
      alert('Failed to reject order');
    }
  };

  const handleUpdateStatus = async (orderId: string, newStatus: string) => {
    try {
      await storeService.updateOrderStatus(orderId, newStatus);
      alert('Order status updated');
      fetchOrders();
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status');
    }
  };

  // Custom Order Bill Functions
  const openCustomBillModal = (order: any) => {
    setCustomBillOrder(order);
    // Pre-fill bill items from customer request
    const requestedItems = order.customOrderRequest?.requestedItems || [];
    const initialItems = requestedItems.length > 0 
      ? requestedItems.map((item: any) => ({
          name: item.name || '',
          quantity: parseInt(item.quantity) || 1,
          price: 0,
          isAvailable: true
        }))
      : [{ name: '', quantity: 1, price: 0, isAvailable: true }];
    setBillItems(initialItems);
    setAdditionalCharges(0);
    setDiscount(0);
    setBillNotes('');
    setShowCustomBill(true);
  };

  const addBillItem = () => {
    setBillItems([...billItems, { name: '', quantity: 1, price: 0, isAvailable: true }]);
  };

  const removeBillItem = (index: number) => {
    if (billItems.length > 1) {
      setBillItems(billItems.filter((_, i) => i !== index));
    }
  };

  const updateBillItem = (index: number, field: string, value: any) => {
    const updated = [...billItems];
    updated[index] = { ...updated[index], [field]: value };
    setBillItems(updated);
  };

  const calculateBillTotal = () => {
    const subtotal = billItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    return subtotal + additionalCharges - discount + 50; // 50 is delivery charge
  };

  const sendCustomBill = async () => {
    if (!customBillOrder) return;
    
    const validItems = billItems.filter(item => item.name.trim() && item.price > 0);
    if (validItems.length === 0) {
      alert('Please add at least one item with price');
      return;
    }

    try {
      await storeService.sendCustomBill(customBillOrder._id, {
        items: validItems,
        additionalCharges,
        discount,
        billNotes,
        deliveryCharges: 50
      });
      alert('✅ Bill sent to customer!\n\nThey will receive a notification to confirm the bill.');
      setShowCustomBill(false);
      setCustomBillOrder(null);
      fetchOrders();
    } catch (error) {
      console.error('Error sending custom bill:', error);
      alert('Failed to send bill');
    }
  };

  const formatCurrency = (amount: number) => `₹${amount?.toFixed(2) || '0.00'}`;

  const formatTime = (date: string) => {
    return new Date(date).toLocaleString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      day: '2-digit',
      month: 'short'
    });
  };

  if (loading) {
    return <div className="loading-container"><div className="loading-spinner"></div></div>;
  }

  return (
    <div className="order-management">
      <h1 className="page-title">📦 Order Management</h1>

      {/* Order Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px', marginBottom: '25px' }}>
        <div 
          onClick={() => setFilterStatus('pending')}
          style={{ 
            background: filterStatus === 'pending' ? '#ff6b6b' : '#fff', 
            color: filterStatus === 'pending' ? '#fff' : '#ff6b6b',
            padding: '15px', 
            borderRadius: '15px', 
            textAlign: 'center', 
            cursor: 'pointer',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            border: '2px solid #ff6b6b',
            transition: 'all 0.3s ease'
          }}
        >
          <div style={{ fontSize: '28px', fontWeight: 'bold' }}>{pendingOrders.length}</div>
          <div style={{ fontSize: '12px', marginTop: '5px' }}>🔔 New</div>
        </div>
        <div 
          onClick={() => setFilterStatus('custom')}
          style={{ 
            background: filterStatus === 'custom' ? '#f97316' : '#fff', 
            color: filterStatus === 'custom' ? '#fff' : '#f97316',
            padding: '15px', 
            borderRadius: '15px', 
            textAlign: 'center', 
            cursor: 'pointer',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            border: '2px solid #f97316',
            transition: 'all 0.3s ease'
          }}
        >
          <div style={{ fontSize: '28px', fontWeight: 'bold' }}>{customOrders.length}</div>
          <div style={{ fontSize: '12px', marginTop: '5px' }}>📝 Custom</div>
        </div>
        <div 
          onClick={() => setFilterStatus('ongoing')}
          style={{ 
            background: filterStatus === 'ongoing' ? '#ffa502' : '#fff', 
            color: filterStatus === 'ongoing' ? '#fff' : '#ffa502',
            padding: '15px', 
            borderRadius: '15px', 
            textAlign: 'center', 
            cursor: 'pointer',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            border: '2px solid #ffa502',
            transition: 'all 0.3s ease'
          }}
        >
          <div style={{ fontSize: '28px', fontWeight: 'bold' }}>{ongoingOrders.length}</div>
          <div style={{ fontSize: '12px', marginTop: '5px' }}>👨‍🍳 Ongoing</div>
        </div>
        <div 
          onClick={() => setFilterStatus('ready')}
          style={{ 
            background: filterStatus === 'ready' ? '#22c55e' : '#fff', 
            color: filterStatus === 'ready' ? '#fff' : '#22c55e',
            padding: '15px', 
            borderRadius: '15px', 
            textAlign: 'center', 
            cursor: 'pointer',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            border: '2px solid #22c55e',
            transition: 'all 0.3s ease'
          }}
        >
          <div style={{ fontSize: '28px', fontWeight: 'bold' }}>{readyOrders.length}</div>
          <div style={{ fontSize: '12px', marginTop: '5px' }}>✅ Ready</div>
        </div>
        <div 
          onClick={() => setFilterStatus('completed')}
          style={{ 
            background: filterStatus === 'completed' ? '#5d8ed5' : '#fff', 
            color: filterStatus === 'completed' ? '#fff' : '#5d8ed5',
            padding: '15px', 
            borderRadius: '15px', 
            textAlign: 'center', 
            cursor: 'pointer',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            border: '2px solid #5d8ed5',
            transition: 'all 0.3s ease'
          }}
        >
          <div style={{ fontSize: '28px', fontWeight: 'bold' }}>{completedOrders.length}</div>
          <div style={{ fontSize: '12px', marginTop: '5px' }}>📋 Done</div>
        </div>
      </div>

      {/* Section Title */}
      <h2 style={{ 
        fontSize: '20px', 
        marginBottom: '15px', 
        color: '#333',
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
      }}>
        {filterStatus === 'pending' && '🔔 New Orders - Accept or Reject'}
        {filterStatus === 'custom' && '📝 Custom Orders - Create Bill'}
        {filterStatus === 'ongoing' && '👨‍🍳 Ongoing Orders - Being Prepared'}
        {filterStatus === 'ready' && '✅ Ready for Pickup'}
        {filterStatus === 'completed' && '📋 Completed Orders'}
      </h2>

      {/* Orders Grid */}
      {(() => {
        const displayOrders = 
          filterStatus === 'pending' ? pendingOrders :
          filterStatus === 'custom' ? customOrders :
          filterStatus === 'ongoing' ? ongoingOrders :
          filterStatus === 'ready' ? readyOrders :
          completedOrders;

        if (displayOrders.length === 0) {
          return (
            <div className="empty-orders">
              <div className="empty-icon">📦</div>
              <p>No {filterStatus} orders</p>
            </div>
          );
        }

        return (
          <div className="orders-grid">
            {displayOrders.map((order) => (
            <div key={order._id} className="order-card">
              <div className="order-header">
                <div className="order-number">#{order.orderId || order._id.slice(-6)}</div>
                <div className={`status-badge status-${order.isCustomOrder ? 'custom' : order.status}`}>
                  {order.isCustomOrder ? `📝 ${order.customOrderStatus || 'Custom'}` : order.status}
                </div>
              </div>

              <div className="order-time">{formatTime(order.createdAt)}</div>

              <div className="order-customer-info">
                <div>👤 {order.userId?.name || order.user?.name || 'Customer'}</div>
                <div>📱 {order.userId?.phone || order.user?.phone || 'N/A'}</div>
              </div>

              {/* Custom Order Request Details */}
              {order.isCustomOrder && order.customOrderRequest && (
                <div style={{ 
                  background: '#fef3c7', 
                  padding: '12px', 
                  borderRadius: '8px', 
                  marginBottom: '10px',
                  border: '1px solid #fbbf24'
                }}>
                  <div style={{ fontWeight: '600', marginBottom: '8px', color: '#92400e' }}>📋 Customer Request:</div>
                  {order.customOrderRequest.description && (
                    <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#78350f' }}>
                      {order.customOrderRequest.description}
                    </p>
                  )}
                  {order.customOrderRequest.requestedItems?.length > 0 && (
                    <div style={{ fontSize: '13px', color: '#78350f' }}>
                      {order.customOrderRequest.requestedItems.map((item: any, idx: number) => (
                        <div key={idx}>• {item.name} {item.quantity && `- ${item.quantity}`}</div>
                      ))}
                    </div>
                  )}
                  {order.customOrderRequest.customerNotes && (
                    <div style={{ marginTop: '8px', fontSize: '13px', fontStyle: 'italic', color: '#92400e' }}>
                      💬 Note: {order.customOrderRequest.customerNotes}
                    </div>
                  )}
                </div>
              )}

              {/* Regular Order Items */}
              {!order.isCustomOrder && (
                <div className="order-items-list">
                  {order.items?.map((item: any, idx: number) => (
                    <div key={idx} className="order-item">
                      <span>{item.quantity}x {item.name}</span>
                      <span>{formatCurrency(item.price * item.quantity)}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Custom Bill Sent Info */}
              {order.isCustomOrder && order.customOrderBill && order.customOrderStatus === 'Bill Sent' && (
                <div style={{ 
                  background: '#dcfce7', 
                  padding: '10px', 
                  borderRadius: '8px', 
                  marginBottom: '10px',
                  border: '1px solid #86efac'
                }}>
                  <div style={{ fontWeight: '600', color: '#166534' }}>✅ Bill Sent: {formatCurrency(order.customOrderBill.finalAmount)}</div>
                  <div style={{ fontSize: '13px', color: '#15803d' }}>Waiting for customer confirmation...</div>
                </div>
              )}

              <div className="order-total">
                <span>Total:</span>
                <span className="total-amount">{formatCurrency(order.totalAmount)}</span>
              </div>

              {/* Customer Contact Buttons */}
              {(order.userId?.phone || order.user?.phone) && (
                <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                  <a 
                    href={`tel:${order.userId?.phone || order.user?.phone}`}
                    style={{
                      flex: 1,
                      padding: '10px',
                      background: '#dcfce7',
                      color: '#166534',
                      textDecoration: 'none',
                      borderRadius: '8px',
                      textAlign: 'center',
                      fontSize: '14px',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '5px'
                    }}
                  >
                    📞 Call
                  </a>
                  <a 
                    href={`https://wa.me/91${(order.userId?.phone || order.user?.phone || '').replace(/\D/g, '').slice(-10)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      flex: 1,
                      padding: '10px',
                      background: '#25D366',
                      color: 'white',
                      textDecoration: 'none',
                      borderRadius: '8px',
                      textAlign: 'center',
                      fontSize: '14px',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '5px'
                    }}
                  >
                    💬 WhatsApp
                  </a>
                </div>
              )}

              {/* Action Buttons */}
              <div className="order-actions">
                {/* Custom Order - Create Bill Button */}
                {order.isCustomOrder && (order.customOrderStatus === 'Requested' || order.customOrderStatus === 'Store Reviewing') && (
                  <button 
                    className="btn-accept"
                    onClick={() => openCustomBillModal(order)}
                    style={{ background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)' }}
                  >
                    💰 Create Bill
                  </button>
                )}

                {/* Regular Order Accept/Reject */}
                {!order.isCustomOrder && (order.status === 'pending' || order.status === 'Pending') ? (
                  <>
                    <button 
                      className="btn-accept"
                      onClick={() => setSelectedOrder(order)}
                    >
                      ✅ Accept
                    </button>
                    <button 
                      className="btn-reject"
                      onClick={() => setSelectedOrder(order)}
                    >
                      ❌ Reject
                    </button>
                  </>
                ) : null}

                {(order.status === 'confirmed' || order.status === 'Confirmed') && (
                  <button 
                    className="btn-primary"
                    onClick={() => handleUpdateStatus(order._id, 'Preparing')}
                  >
                    👨‍🍳 Start Preparing
                  </button>
                )}

                {(order.status === 'preparing' || order.status === 'Preparing') && (
                  <button 
                    className="btn-primary"
                    onClick={() => handleUpdateStatus(order._id, 'Ready')}
                  >
                    ✅ Mark as Ready
                  </button>
                )}

                {(order.status === 'ready' || order.status === 'Ready') && (
                  <button className="btn-primary" style={{ background: '#22c55e' }}>
                    📦 Waiting for Partner
                  </button>
                )}

                {(order.status === 'delivered' || order.status === 'Delivered' || order.status === 'rejected' || order.status === 'Rejected') && (
                  <button className="btn-disabled" disabled>
                    {order.status.toLowerCase() === 'delivered' ? '✅ Completed' : '❌ Rejected'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
        );
      })()}

      {/* Accept/Reject Modal */}
      {selectedOrder && (
        <div className="modal-overlay" onClick={() => setSelectedOrder(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Order #{selectedOrder.orderId || selectedOrder._id.slice(-6)}</h2>
            
            <div className="modal-section">
              <label>Preparation Time (minutes):</label>
              <input
                type="number"
                value={prepTime}
                onChange={(e) => setPrepTime(parseInt(e.target.value) || 15)}
                min="5"
                max="120"
                className="prep-time-input"
              />
            </div>

            <div className="modal-section">
              <label>Rejection Reason:</label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter reason for rejection..."
                className="reject-textarea"
                rows={3}
              />
            </div>

            <div className="modal-actions">
              <button 
                className="btn-accept-modal"
                onClick={() => handleAcceptOrder(selectedOrder._id)}
              >
                ✅ Accept Order ({prepTime} min)
              </button>
              <button 
                className="btn-reject-modal"
                onClick={() => handleRejectOrder(selectedOrder._id)}
                disabled={!rejectReason.trim()}
              >
                ❌ Reject Order
              </button>
              <button 
                className="btn-cancel"
                onClick={() => {
                  setSelectedOrder(null);
                  setRejectReason('');
                  setPrepTime(15);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Bill Modal */}
      {showCustomBill && customBillOrder && (
        <div className="modal-overlay" onClick={() => setShowCustomBill(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px', maxHeight: '90vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0 }}>💰 Create Bill</h2>
              <button
                onClick={() => setShowCustomBill(false)}
                style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}
              >
                ✕
              </button>
            </div>

            <p style={{ color: '#666', marginBottom: '15px' }}>
              Order #{customBillOrder.orderId || customBillOrder._id?.slice(-6)} - Custom Order Bill
            </p>

            {/* Customer Request Summary */}
            {customBillOrder.customOrderRequest && (
              <div style={{ background: '#fef3c7', padding: '12px', borderRadius: '8px', marginBottom: '20px' }}>
                <strong>Customer Request:</strong>
                <p style={{ margin: '5px 0 0 0', fontSize: '14px' }}>
                  {customBillOrder.customOrderRequest.description}
                </p>
              </div>
            )}

            {/* Bill Items */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ fontWeight: '600', display: 'block', marginBottom: '10px' }}>📦 Add Items with Price:</label>
              
              {/* Header Row */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', fontSize: '12px', color: '#666', fontWeight: '600' }}>
                <span style={{ flex: 2 }}>Item Name</span>
                <span style={{ width: '60px', textAlign: 'center' }}>Qty</span>
                <span style={{ width: '90px', textAlign: 'center' }}>₹ Price</span>
                <span style={{ width: '70px', textAlign: 'center' }}>Total</span>
                <span style={{ width: '36px' }}></span>
              </div>

              {billItems.map((item, index) => (
                <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '10px', alignItems: 'center' }}>
                  <input
                    type="text"
                    value={item.name}
                    onChange={(e) => updateBillItem(index, 'name', e.target.value)}
                    placeholder="Enter item name"
                    style={{ flex: 2, padding: '10px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px' }}
                  />
                  <input
                    type="number"
                    value={item.quantity}
                    onChange={(e) => updateBillItem(index, 'quantity', parseInt(e.target.value) || 1)}
                    placeholder="1"
                    min="1"
                    style={{ width: '60px', padding: '10px', borderRadius: '8px', border: '1px solid #ddd', textAlign: 'center', fontSize: '14px' }}
                  />
                  <div style={{ position: 'relative', width: '90px' }}>
                    <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#666' }}>₹</span>
                    <input
                      type="number"
                      value={item.price || ''}
                      onChange={(e) => updateBillItem(index, 'price', parseFloat(e.target.value) || 0)}
                      placeholder="0"
                      min="0"
                      style={{ width: '100%', padding: '10px 10px 10px 25px', borderRadius: '8px', border: '1px solid #ddd', textAlign: 'right', fontSize: '14px' }}
                    />
                  </div>
                  <span style={{ width: '70px', textAlign: 'right', fontWeight: '600', color: '#667eea', fontSize: '14px' }}>
                    ₹{(item.price * item.quantity).toFixed(0)}
                  </span>
                  {billItems.length > 1 ? (
                    <button
                      onClick={() => removeBillItem(index)}
                      style={{ background: '#fee2e2', border: 'none', borderRadius: '8px', width: '36px', height: '36px', cursor: 'pointer', color: '#dc2626', fontSize: '16px' }}
                    >
                      ✕
                    </button>
                  ) : (
                    <span style={{ width: '36px' }}></span>
                  )}
                </div>
              ))}
              <button
                onClick={addBillItem}
                style={{ width: '100%', padding: '10px', background: '#f3f4f6', border: '1px dashed #9ca3af', borderRadius: '8px', cursor: 'pointer' }}
              >
                + Add Item
              </button>
            </div>

            {/* Additional Charges & Discount */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>Additional Charges:</label>
                <input
                  type="number"
                  value={additionalCharges}
                  onChange={(e) => setAdditionalCharges(parseFloat(e.target.value) || 0)}
                  min="0"
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>Discount:</label>
                <input
                  type="number"
                  value={discount}
                  onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)}
                  min="0"
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }}
                />
              </div>
            </div>

            {/* Notes */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>Notes for customer:</label>
              <textarea
                value={billNotes}
                onChange={(e) => setBillNotes(e.target.value)}
                placeholder="e.g., Some items were not available..."
                rows={2}
                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }}
              />
            </div>

            {/* Bill Summary */}
            <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '10px', marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span>Subtotal:</span>
                <span>{formatCurrency(billItems.reduce((sum, item) => sum + (item.price * item.quantity), 0))}</span>
              </div>
              {additionalCharges > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span>Additional Charges:</span>
                  <span>+{formatCurrency(additionalCharges)}</span>
                </div>
              )}
              {discount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', color: '#22c55e' }}>
                  <span>Discount:</span>
                  <span>-{formatCurrency(discount)}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span>Delivery Charges:</span>
                <span>₹50.00</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: '700', fontSize: '18px', borderTop: '1px solid #ddd', paddingTop: '10px' }}>
                <span>Total:</span>
                <span style={{ color: '#667eea' }}>{formatCurrency(calculateBillTotal())}</span>
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={sendCustomBill}
                style={{
                  flex: 1,
                  padding: '15px',
                  background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  fontWeight: '700',
                  cursor: 'pointer'
                }}
              >
                📤 Send Bill to Customer
              </button>
              <button
                onClick={() => setShowCustomBill(false)}
                style={{
                  padding: '15px 25px',
                  background: '#f3f4f6',
                  color: '#666',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderManagement;
